/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useEffect } from 'react'

import {
  EuiSkeletonText,
  EuiSwitch,
  EuiSpacer,
  EuiPanel,
  EuiFormRow,
  EuiDescribedFormGroup,
  EuiCallOut,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AccountResponse, AccountUpdateRequest } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState } from '@modules/ui-types'
import { CuiAlert } from '@modules/cui/Alert'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

export type Props = {
  currentAccount: AccountResponse | null
  fetchCurrentAccount: () => void
  updateCurrentAccount: (payload: AccountUpdateRequest) => void
  fetchCurrentAccountRequest: AsyncRequestState
  updateCurrentAccountRequest: AsyncRequestState
  resetUpdateCurrentAccount: () => void
  resetFetchCurrentAccount: () => void
}

const SetDefaultTrust = (props: Props): JSX.Element => {
  const {
    currentAccount,
    updateCurrentAccount,
    fetchCurrentAccount,
    fetchCurrentAccountRequest,
    updateCurrentAccountRequest,
    resetFetchCurrentAccount,
    resetUpdateCurrentAccount,
  } = props

  useEffect(() => {
    fetchCurrentAccount()

    return () => {
      resetFetchCurrentAccount()
      resetUpdateCurrentAccount()
    }
  }, [fetchCurrentAccount, resetFetchCurrentAccount, resetUpdateCurrentAccount])

  if (fetchCurrentAccountRequest.error) {
    return (
      <Fragment>
        <CuiAlert type='error' data-test-id='fetch-current-account-request-error'>
          {fetchCurrentAccountRequest.error}
        </CuiAlert>

        <EuiSpacer size='s' />
      </Fragment>
    )
  }

  if (!currentAccount) {
    return <EuiSkeletonText data-test-id='current-account-loading' />
  }

  const trustAll = Boolean(currentAccount.trust?.trust_all)

  return (
    <PermissionsGate
      permissions={[
        {
          type: 'account-info',
          action: 'update',
          id: currentAccount.id,
        },
      ]}
    >
      {({ hasPermissions }) => (
        <EuiPanel hasShadow={false} hasBorder={true}>
          <EuiDescribedFormGroup
            style={{
              flexDirection: 'column',
            }}
            title={
              <EuiText>
                <EuiSpacer size='m' />
                <strong>
                  <FormattedMessage
                    id='trust-management.default-trust-title'
                    defaultMessage='Hosted deployment'
                  />
                </strong>
              </EuiText>
            }
            titleSize='xxs'
            description={
              <FormattedMessage
                id='trust-management.default-trust-description'
                defaultMessage='Select the default trust level between new deployments and other deployments of this organization.'
              />
            }
          >
            <EuiFormRow
              helpText={
                <EuiText css={{ paddingLeft: 53 }}>
                  <FormattedMessage
                    id='trust-management.default-trust-text'
                    defaultMessage='This configuration also includes future deployments.'
                  />
                </EuiText>
              }
            >
              <EuiSwitch
                label={
                  <FormattedMessage
                    id='trust-management.default-trust-switch-label'
                    defaultMessage='Trust all deployments'
                  />
                }
                checked={trustAll}
                data-test-id='set-default-trust'
                data-test-subj='trust'
                disabled={updateCurrentAccountRequest.inProgress || !hasPermissions}
                onChange={(e) => {
                  updateCurrentAccount({
                    trust: {
                      trust_all: e.target.checked,
                    },
                  })
                }}
              />
            </EuiFormRow>
          </EuiDescribedFormGroup>
          <EuiCallOut
            iconType='info'
            size='s'
            title={
              <FormattedMessage
                id='trust-management.default-trust-callout'
                defaultMessage='You can set trust levels for specific deployments on the Security page.'
              />
            }
          />
          {updateCurrentAccountRequest.error && (
            <Fragment>
              <CuiAlert type='error' data-test-id='update-current-account-request-error'>
                {updateCurrentAccountRequest.error}
              </CuiAlert>

              <EuiSpacer size='s' />
            </Fragment>
          )}
        </EuiPanel>
      )}
    </PermissionsGate>
  )
}

export default SetDefaultTrust
